import React, { useState, useEffect, useRef } from "react";
import {
  Check,
  X as XIcon,
  Eye,
  QrCode,
  Search,
  MapPin,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  ShieldCheck,
  Clock,
  UploadCloud,
  Maximize2,
  MessageSquare,
  CreditCard,
  CheckCircle2,
  Receipt,
  CalendarDays,
} from "lucide-react";
import Swal from "sweetalert2";
import api, { DRIVER_IMAGE_URL, IMAGE_BASE_URL } from "../api/Api";
import * as XLSX from "xlsx";
import imageCompression from "browser-image-compression";

// Helper for date formatting
const getFormattedDate = (dateObj) => dateObj.toISOString().split("T")[0];

const AdminRefuelVerify = () => {
  // ========================== 1. STATES ==========================
  const [allRequests, setAllRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("pending"); // pending | approved | verified

  // Date Filter States (Default: Last 7 Days)
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return getFormattedDate(d);
  });
  const [endDate, setEndDate] = useState(() => getFormattedDate(new Date()));
  const maxDate = getFormattedDate(new Date()); // Block future dates

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Image Viewing Modals
  const [viewImages, setViewImages] = useState(null);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [viewQR, setViewQR] = useState(null);

  // Approval Modal (For Pending Tab)
  const [showApproveModal, setShowApproveModal] = useState(null);
  const [approvalType, setApprovalType] = useState("otp"); // 'otp' | 'payment'
  const [approveNote, setApproveNote] = useState("");
  const [approveFile, setApproveFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const approveFileInputRef = useRef(null);

  // ========================== 2. FETCH & EFFECTS ==========================
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/drvexpence/all-refuels");
      if (res.data.success) {
        // console.log("Fetched Refuel Data:", res.data.data);
        setAllRequests(res.data.data);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      Swal.fire("Error", "Failed to load data.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter Logic
  useEffect(() => {
    let data = allRequests;

    // 1. Filter by Date Range
    if (startDate && endDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      data = data.filter((r) => {
        const itemDate = new Date(r.date);
        return itemDate >= start && itemDate <= end;
      });
    }

    // 2. Filter by Tab
    if (activeTab === "pending") {
      data = data.filter((r) => r.exp_status === 0);
    } else if (activeTab === "approved") {
      data = data.filter((r) => r.exp_status === 1 && r.is_verified === 0);
    } else if (activeTab === "verified") {
      data = data.filter((r) => r.is_verified === 1);
    }

    // 3. Filter by Search (Updated to include booking_id, ref_no, cust_name, mob_no)
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      data = data.filter(
        (req) =>
          (req.booking_id || "").toLowerCase().includes(lowerTerm) ||
          (req.booking_ref_no || "").toLowerCase().includes(lowerTerm) ||
          (req.cust_name || req.driver?.name || "").toLowerCase().includes(lowerTerm) ||
          (req.cust_mob_no || req.driver?.regno || "").toLowerCase().includes(lowerTerm) ||
          (req.expense_no || "").toLowerCase().includes(lowerTerm)
      );
    }

    setFilteredRequests(data);
    setCurrentPage(1);
  }, [activeTab, searchTerm, allRequests, startDate, endDate]);

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  // ========================== 3. ACTION HANDLERS ==========================

 const handleExportExcel = () => {
    if (filteredRequests.length === 0)
      return Swal.fire("Info", "No data to export.", "info");

    const dataToExport = filteredRequests.map((item) => {
      // Determine Status text matching the UI badges
      let statusText = "Pending";
      if (item.is_verified === 1) {
        statusText = Number(item.paid_thru) === 16 ? "Auto Verified (Cash)" : "Admin Verified";
      } else if (item.exp_status === 1) {
        statusText = "Approved";
      }

      // Base Excel Row restructured to match the new UI columns
      const rowData = {
        "Expense No": item.expense_no || "-",
        "Expense Date": new Date(item.date).toLocaleDateString(),
        
        "Customer Name": item.cust_name || "Unknown",
        "Customer Mobile": item.cust_mob_no || "-",
        
        "Driver Name": item.driver?.name || "Unknown",
        "Driver Mobile": item.driver?.phone || "-",
        
        "Amount (₹)": Number(item.amount).toFixed(2),
        "Liters": item.liters,
        "Rate (₹)": item.rate,
        
        "Trip ID": item.booking_id || "-",
        "Location": item.location || "-",
      };

      // Add Approved Date column only if in Approved tab
      if (activeTab === "approved") {
        rowData["Approved On"] = item.approve_date 
          ? new Date(item.approve_date).toLocaleString("en-IN") 
          : "-";
      }

      // Add Verified Date column only if in Verified tab
      if (activeTab === "verified") {
        rowData["Verified On"] = item.verified_date 
          ? new Date(item.verified_date).toLocaleString("en-IN") 
          : "-";
      }

      // Append Status at the very end
      rowData["Status"] = statusText;

      return rowData;
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Refuel Report");
    XLSX.writeFile(
      wb,
      `Refuel_${activeTab}_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}.xlsx`
    );
  };

  const handleReject = async (id) => {
    const adminId = localStorage.getItem("SessionUserId");
    if (!adminId) return Swal.fire("Error", "Session Expired.", "error");

    const result = await Swal.fire({
      title: "Reject Request?",
      text: "This will mark the request as rejected.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, Reject",
    });

    if (result.isConfirmed) {
      try {
        const res = await api.post("/drvexpence/verify-refuel", {
          expense_id: id,
          action: "reject",
          admin_id: adminId,
        });
        if (res.data.success) {
          Swal.fire("Rejected", "Request has been rejected.", "success");
          fetchData();
        }
      } catch (err) {
        Swal.fire("Error", "Action failed", "error");
      }
    }
  };

  const handleApproveFileChange = (event) => {
    const file = event.target.files[0];
    if (file) setApproveFile(file);
  };

  const submitApproveAndPay = async () => {
    const adminId = localStorage.getItem("SessionUserId");
    if (!adminId) return Swal.fire("Error", "Session Expired.", "error");

    if (approvalType === "payment" && !approveFile) {
      return Swal.fire("Warning", "Please upload the payment screenshot.", "warning");
    }
    if (approvalType === "otp" && !approveNote.trim()) {
      return Swal.fire("Warning", "Please enter the OTP or comments.", "warning");
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("expense_id", showApproveModal.id);
      formData.append("action", "approve");
      formData.append("admin_id", adminId);
      formData.append("is_paid_flow", "true");
      formData.append("payment_mode_type", approvalType);
      formData.append("admin_note", approveNote);

      if (approvalType === "payment" && approveFile) {
        const options = { maxSizeMB: 1, maxWidthOrHeight: 1024, useWebWorker: true };
        const compressedFile = await imageCompression(approveFile, options);
        formData.append("payment_photo", compressedFile);
      }

      const res = await api.post("/drvexpence/verify-refuel", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        Swal.fire({
          icon: "success",
          title: "Approved & Paid",
          text: "Expense Successfully Approved and Paid.",
          timer: 1500,
          showConfirmButton: false,
        });
        closeApproveModal();
        fetchData();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Approval failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeApproveModal = () => {
    setShowApproveModal(null);
    setApproveNote("");
    setApproveFile(null);
    setApprovalType("otp");
  };

  const handleVerifyDirectly = async (id) => {
    const adminId = localStorage.getItem("SessionUserId");
    if (!adminId) return Swal.fire("Error", "Session Expired.", "error");

    const result = await Swal.fire({
      title: "Verify this Expense?",
      text: "Are you sure you want to mark this as finally Verified?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Verify",
    });

    if (result.isConfirmed) {
      try {
        const res = await api.post("/drvexpence/verify-refuel", {
          expense_id: id,
          action: "verify",
          admin_id: adminId,
        });

        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Verified!",
            text: "Expense marked as Verified.",
            timer: 1500,
            showConfirmButton: false,
          });
          fetchData();
        }
      } catch (err) {
        Swal.fire("Error", err.response?.data?.message || "Verification failed", "error");
      }
    }
  };

  const getImgUrl = (filename, type) => {
    if (!filename) return "https://placehold.co/400?text=No+Img";
    if (type === "payment_proof") {
      return `${IMAGE_BASE_URL}/uploads/refuel_receipt/${filename}`;
    }
    return `${DRIVER_IMAGE_URL}/uploads/refuel_receipt/${filename}`;
  };

  // ========================== 4. RENDER ==========================
  return (
    <div className="container-fluid bg-white vh-100 p-0 p-md-0 font-sans d-flex flex-column overflow-hidden">
      
      {/* --- HEADER --- */}
      <div className="flex-shrink-0 px-2 px-md-2 pt-2 pt-md-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h4 className="fw-bold text-dark mb-1">Refuel Expense Management</h4>
            <p className="text-muted small mb-0">Track, Approve, and Verify fuel expenses</p>
          </div>
          <button
            onClick={handleExportExcel}
            className="btn-icon-circle text-success shadow-sm"
            title="Export Excel"
          >
            <FileSpreadsheet size={22} />
          </button>
        </div>

        {/* Tabs, Date Filter, and Search */}
        <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
          {/* Tabs */}
          <div className="bg-white p-1 rounded-pill d-inline-flex shadow-sm border overflow-auto">
            {[
              { id: "pending", label: "Pending", count: allRequests.filter((r) => r.exp_status === 0).length },
              { id: "approved", label: "Approved", count: allRequests.filter((r) => r.exp_status === 1 && r.is_verified === 0).length },
              { id: "verified", label: "Verified", count: allRequests.filter((r) => r.exp_status === 1 && r.is_verified === 1).length },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
              >
                {tab.label}
                <span className={`count-badge ${activeTab === tab.id ? "active" : ""}`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Date Filter */}
          <div className="d-flex align-items-center gap-2 bg-white-subtle px-3 py-1 rounded-pill border shadow-sm date-filter-pill">
            <CalendarDays size={16} className="text-muted me-1" />
            <div className="d-flex align-items-center">
              <span className="text-muted extra-small me-1 fw-bold">FROM</span>
              <input
                type="date"
                className="date-input"
                value={startDate}
                max={maxDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="border-start" style={{ height: "16px" }}></div>
            <div className="d-flex align-items-center">
              <span className="text-muted extra-small ms-2 me-1 fw-bold">TO</span>
              <input
                type="date"
                className="date-input"
                value={endDate}
                max={maxDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* Search Bar */}
          <div className="search-pill shadow-sm ms-auto">
            <Search size={16} className="text-muted ms-3 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search Booking ID, Ref No..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent"
            />
          </div>
        </div>
      </div>

      {/* --- TABLE CONTAINER --- */}
     <div className="card border-0 shadow-custom rounded-4 overflow-hidden bg-white flex-grow-1 d-flex flex-column m-1 m-md-2 mt-1">
  <div className="table-responsive flex-grow-1" style={{ overflowY: "auto" }}>
    {/* ADDED 'table-sm' for tighter row spacing */}
    <table className="table table-sm align-middle mb-0 custom-table text-nowrap">
      <thead className="bg-success-subtle sticky-top shadow-sm-bottom">
        <tr>
          {/* Reduced horizontal padding ps-4 to ps-2 */}
          <th className="ps-2 py-2">EXP./DATE</th>
          <th className="py-2">CUSTOMER/MOB</th>
          <th className="py-2">DRIVER/MOB</th>
          <th className="py-2">AMOUNT</th>
          <th className="py-2">TRIP/LOCATION</th>
          {activeTab === "approved" && <th className="py-2">APPROVED ON</th>}
          {activeTab === "verified" && <th className="py-2">VERIFIED ON</th>}
          <th className="text-center py-2">PROOFS</th>
          {/* Reduced pe-4 to pe-2 */}
          <th className="pe-2 text-end py-2">ACTION</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            {/* Reduced py-5 to py-3 */}
            <td colSpan="8" className="text-center py-3">
              <div className="spinner-border text-primary spinner-border-sm"></div>
            </td>
          </tr>
        ) : currentItems.length === 0 ? (
          <tr>
            <td colSpan="8" className="text-center py-4 text-muted">
              <ShieldCheck size={30} className="mb-2 opacity-50 mx-auto" />
              <p className="mb-0 small">No records found in this category.</p>
            </td>
          </tr>
        ) : (
          currentItems.map((item) => (
            <tr key={item.id} className="hover-row">
              
              {/* Column 1: Booking ID & Ref No */}
              <td className="ps-2">
                <div className="d-flex flex-column lh-sm">
                  <span className="fw-bold text-dark text-uppercase" style={{fontSize: "0.8rem"}}>
                    {item.expense_no || "-"}
                  </span>
                  {/* {(item.booking_ref_no ) && (
                    <span className="text-muted mt-1 d-flex align-items-center gap-1">
                      <span className="badge bg-light text-secondary border px-1 py-0" style={{fontSize: "0.60rem"}}>
                        {item.booking_ref_no}
                      </span>
                    </span>
                  )} */}
                  <span className="text-dark mt-1" style={{fontSize: "0.65rem"}}>
                    {new Date(item.date).toLocaleDateString()}
                  </span>
                </div>
              </td>

              {/* Column 2: Customer Name & Mobile */}
              <td>
                <div className="d-flex flex-column lh-sm">
                  <span className="fw-bold text-dark" style={{fontSize: "0.8rem"}}>
                    {item.cust_name || "Unknown"}
                  </span>
                  <span className="text-muted mt-1 d-flex align-items-center gap-1">
                    <span className="badge bg-info-subtle text-primary border border-primary-subtle px-1 py-0" style={{fontSize: "0.60rem"}}>
                     {item.cust_mob_no || "-"}
                    </span>
                  </span>
                </div>
              </td>
              
              {/* Column 3: Driver & Mobile */}
              <td>
                <div className="d-flex flex-column lh-sm">
                  <span className="fw-bold text-dark" style={{fontSize: "0.8rem"}}>
                    {item.driver?.name || "Unknown"}
                  </span>
                  <span className="text-muted mt-1 d-flex align-items-center gap-1">
                    <span className="badge bg-info-subtle text-primary border border-primary-subtle px-1 py-0" style={{fontSize: "0.60rem"}}>
                     {item.driver?.phone || "-"}
                    </span>
                  </span>
                </div>
              </td>

              {/* Amount */}
              <td>
                <div className="d-flex flex-column lh-sm">
                <span className="fw-bolder text-dark" style={{ fontSize: "0.85rem" }}>
                ₹{Number(item.amount).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
                  <span className="text-muted fw-medium mt-1" style={{ fontSize: "0.65rem" }}>
                    {item.liters} L <span className="mx-1 text-secondary opacity-50">×</span> ₹{item.rate}
                  </span>
                </div>
              </td>

              {/* Location */}
              <td>
                <div className="d-flex flex-column justify-content-center gap-0 lh-sm">
                  {/* Expense Number */}
                  <span className="fw-bold text-dark mb-1" style={{ fontSize: "0.80rem" }}>
                    {item.booking_id}
                  </span>
                  
                  {/* Location with Pin */}
                  <div className="location-cell d-flex align-items-center gap-1">
                    <MapPin size={11} className="text-primary flex-shrink-0" />
                    <span 
                      className="text-truncate" 
                      style={{ maxWidth: "140px", fontSize: "0.70rem" }} 
                      title={item.location}
                    >
                      {item.location}
                    </span>
                  </div>
                </div>
              </td>

              {/* Approved Date */}
              {activeTab === "approved" && (
                <td>
                  <div className="d-flex flex-column lh-sm">
                    <div className="text-dark fw-bold" style={{ fontSize: "0.75rem" }}>
                      {item.approve_date ? new Date(item.approve_date).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-"}
                    </div>
                    <div className="text-muted d-flex align-items-center gap-1 mt-1" style={{ fontSize: "0.65rem" }}>
                      <Clock size={9} className="text-primary" />
                      {item.approve_date ? new Date(item.approve_date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : ""}
                    </div>
                  </div>
                </td>
              )}

              {/* Verified Date */}
              {activeTab === "verified" && (
                <td>
                  <div className="d-flex flex-column lh-sm">
                    <div className="text-dark fw-bold" style={{ fontSize: "0.75rem" }}>
                      {item.verified_date ? new Date(item.verified_date).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-"}
                    </div>
                    <div className="text-muted d-flex align-items-center gap-1 mt-1" style={{ fontSize: "0.65rem" }}>
                      <Clock size={9} className="text-primary" />
                      {item.verified_date ? new Date(item.verified_date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : ""}
                    </div>
                  </div>
                </td>
              )}

              {/* Photos / QR */}
              <td className="text-center">
                <div className="d-flex justify-content-center gap-1">
                  <button className="btn-icon-soft" style={{ width: "26px", height: "26px" }} onClick={() => setViewImages(item.images)} title="Driver Meter Photos">
                    <Eye size={13} />
                  </button>
                  {item.payment_mode === "Office" && item.images?.qr && (
                    <button className="btn-icon-soft text-indigo" style={{ width: "26px", height: "26px" }} onClick={() => setViewQR(item.images.qr)} title="Driver Payment QR">
                      <QrCode size={13} />
                    </button>
                  )}
                  {item.images?.payment_proof && (
                    <button
                      className="btn-icon-soft text-success border-success bg-soft-success"
                      style={{ width: "26px", height: "26px" }}
                      onClick={() => setFullScreenImage(getImgUrl(item.images.payment_proof, "payment_proof"))}
                      title="Admin Payment Receipt"
                    >
                      <Receipt size={13} />
                    </button>
                  )}
                </div>
              </td>

              {/* Actions */}
              <td className="pe-2 text-end">
                {activeTab === "pending" && (
                  <div className="d-flex justify-content-end gap-1">
                    <button className="action-btn approve px-2 py-1" style={{ fontSize: "0.7rem" }} onClick={() => setShowApproveModal(item)}>
                      <Check size={13} /> Procced To Approve
                    </button>
                    {/* <button className="action-btn reject px-2 py-1" onClick={() => handleReject(item.id)}>
                      <XIcon size={13} />
                    </button> */}
                  </div>
                )}
                {activeTab === "approved" && (
                  <button className="action-btn verify d-inline-flex px-2 py-1" style={{ fontSize: "0.7rem" }} onClick={() => handleVerifyDirectly(item.id)}>
                    <CheckCircle2 size={13} /> Verify
                  </button>
                )}
                {activeTab === "verified" && (
                  <div className="d-flex flex-column align-items-end gap-0">
                    <span className={`status-badge ${Number(item.paid_thru) === 16 ? "auto-verified" : "verified"}`} style={{ padding: "2px 6px", fontSize: "0.65rem" }}>
                      {Number(item.paid_thru) === 16 ? "Auto Verified" : "Admin Verified"}
                    </span>
                    {Number(item.paid_thru) === 16 && (
                      <span className="fw-bold text-info opacity-75 mt-1" style={{ fontSize: "8px", letterSpacing: "0.3px" }}>
                        PAID BY CASH
                      </span>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>

  {/* PAGINATION CONTROLS - Reduced padding py-3 to py-2, px-4 to px-3 */}
  {totalPages > 1 && (
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center px-3 py-2 border-top gap-2 bg-light">
      <small className="text-muted fw-medium" style={{ fontSize: "0.75rem" }}>
        Showing Page {currentPage} of {totalPages}
      </small>
      <div className="d-flex gap-1">
        <button className="btn btn-sm btn-white border shadow-sm py-1 px-2" style={{ fontSize: "0.75rem" }} disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
          <ChevronLeft size={14} /> Prev
        </button>
        <button className="btn btn-sm btn-white border shadow-sm py-1 px-2" style={{ fontSize: "0.75rem" }} disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  )}
</div>

      {/* ============ MODAL 1: APPROVAL (Pending Tab) ============ */}
      {showApproveModal && (
        <div className="modal-backdrop-custom" onClick={closeApproveModal}>
          <div className="modal-content-custom zoom-in" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "480px" }}>
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
              <div>
                <h6 className="fw-bold mb-0 text-dark">Approve & Pay Expense</h6>
                <small className="text-dark">Ref: {showApproveModal.booking_id || showApproveModal.expense_no} | Amount: ₹{showApproveModal.amount}</small>
              </div>
              <button className="btn-close-custom" onClick={closeApproveModal}><XIcon size={18} /></button>
            </div>

            <div className="p-4">
              <label className="fw-bold small text-muted mb-2 d-block">SELECT APPROVAL MODE</label>
              <div className="row g-2 mb-4">
                <div className="col-6">
                  <div className={`option-card ${approvalType === "otp" ? "active" : ""}`} onClick={() => setApprovalType("otp")}>
                    <MessageSquare size={22} className="mb-2" />
                    <span>Share OTP / Comments</span>
                  </div>
                </div>
                <div className="col-6">
                  <div className={`option-card ${approvalType === "payment" ? "active" : ""}`} onClick={() => setApprovalType("payment")}>
                    <CreditCard size={22} className="mb-2" />
                    <span>Upload Payment screen</span>
                  </div>
                </div>
              </div>

              {approvalType === "otp" ? (
                <div className="animate-fade-in">
                  <label className="small fw-bold mb-1">OTP / Comment <span className="text-danger">*</span></label>
                  <textarea className="form-control bg-light" rows="3" placeholder="Enter OTP / Comments..." value={approveNote} onChange={(e) => setApproveNote(e.target.value)}></textarea>
                </div>
              ) : (
                <div className="animate-fade-in">
                  <label className="small fw-bold mb-1">Payment Screenshot <span className="text-danger">*</span></label>
                  <div className="mb-3 upload-box" onClick={() => approveFileInputRef.current.click()}>
                    <input type="file" ref={approveFileInputRef} onChange={handleApproveFileChange} hidden accept="image/*" />
                    {approveFile ? (
                      <div className="text-success small fw-bold d-flex align-items-center justify-content-center gap-2">
                        <CheckCircle2 size={18} /> {approveFile.name}
                      </div>
                    ) : (
                      <div className="text-primary small d-flex flex-column align-items-center">
                        <UploadCloud size={24} className="mb-1" />
                        <span>Click to browse</span>
                      </div>
                    )}
                  </div>
                  <label className="small fw-bold mb-1">Note (Optional)</label>
                  <textarea className="form-control bg-light" rows="2" placeholder="Txn ID / Remarks..." value={approveNote} onChange={(e) => setApproveNote(e.target.value)}></textarea>
                </div>
              )}

              <div className="mt-4 pt-2 border-top">
                <button className="btn btn-primary w-100 rounded-pill py-2 fw-bold shadow-sm" onClick={submitApproveAndPay} disabled={isSubmitting}>
                  {isSubmitting ? <span><span className="spinner-border spinner-border-sm me-2"></span>Processing...</span> : <span>Confirm Approval</span>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL 2: VIEW IMAGES GRID ============ */}
      {viewImages && (
        <div className="modal-backdrop-custom" onClick={() => setViewImages(null)}>
          <div className="modal-content-custom zoom-in" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "700px" }}>
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h6 className="fw-bold mb-0">Photo Proofs</h6>
              <button className="btn-close-custom bg-transparent border-0" onClick={() => setViewImages(null)}><XIcon size={18} /></button>
            </div>
            <div className="p-4 bg-light">
              <div className="row g-3">
                {["pump", "tank", "odo"].map((key) => (
                  <div className="col-4" key={key}>
                    <div className="proof-card cursor-pointer" onClick={() => setFullScreenImage(getImgUrl(viewImages[key]))}>
                      <div className="img-wrapper">
                        <img src={getImgUrl(viewImages[key])} alt={key} onError={(e) => (e.target.src = "https://placehold.co/400?text=No+Img")} />
                        <div className="hover-overlay"><Maximize2 size={20} className="text-white" /></div>
                      </div>
                      <div className="label text-uppercase small fw-bold mt-1 text-muted text-center">{key}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL 3: FULL SCREEN IMAGE ============ */}
      {fullScreenImage && (
        <div className="modal-backdrop-custom" style={{ zIndex: 1060, background: "rgba(0,0,0,0.85)" }} onClick={() => setFullScreenImage(null)}>
          <button className="btn-close-floating" onClick={() => setFullScreenImage(null)}><XIcon size={24} /></button>
          <img src={fullScreenImage} className="img-fluid rounded shadow-lg zoom-in" style={{ maxHeight: "90vh", maxWidth: "90vw" }} onClick={(e) => e.stopPropagation()} alt="Full Screen" />
        </div>
      )}

      {/* ============ MODAL 4: QR CODE ============ */}
      {viewQR && (
        <div className="modal-backdrop-custom" onClick={() => setViewQR(null)}>
          <div className="modal-content-custom zoom-in" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "320px" }}>
            <div className="p-4 text-center">
              <h6 className="fw-bold mb-3">Payment QR</h6>
              <div className="bg-white p-2 rounded border d-inline-block mb-3">
                <img src={getImgUrl(viewQR)} className="img-fluid" style={{ maxHeight: "200px" }} alt="QR" onError={(e) => (e.target.src = "https://placehold.co/400?text=No+QR")} onClick={() => setFullScreenImage(getImgUrl(viewQR))} />
              </div>
              <button className="btn btn-dark w-100 rounded-pill py-2" onClick={() => setViewQR(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ============ STYLES ============ */}
      <style>{`
        .bg-surface { background-color: #f8f9fa; }
        .font-sans { font-family: 'Inter', sans-serif; }
        .text-indigo { color: #6366f1; }
        .cursor-pointer { cursor: pointer; }
        .extra-small { font-size: 0.70rem; }

        /* --- Search & Filters --- */
        .search-pill { background: white; border-radius: 50px; padding: 8px 12px; display: flex; align-items: center; border: 1px solid #e5e7eb; width: 100%; max-width: 280px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .search-pill input { border: none; outline: none; width: 100%; margin-left: 10px; font-size: 0.9rem; }
        
        .date-filter-pill { height: 38px; }
        .date-input { border: none; outline: none; background: transparent; font-size: 0.8rem; font-weight: 500; color: #374151; cursor: pointer; }
        .date-input::-webkit-calendar-picker-indicator { cursor: pointer; opacity: 0.6; }

        .btn-icon-circle { width: 40px; height: 40px; border-radius: 50%; border: 1px solid #e5e7eb; background: white; display: flex; align-items: center; justify-content: center; transition: 0.2s; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .btn-icon-circle:hover { background: #f3f4f6; transform: translateY(-1px); }
        
        /* --- Tabs --- */
        .tab-btn { border: none; background: transparent; padding: 8px 18px; border-radius: 50px; font-size: 0.85rem; font-weight: 600; color: #6b7280; display: flex; align-items: center; gap: 8px; transition: 0.2s; white-space: nowrap; }
        .tab-btn:hover { color: #111827; background: #f3f4f6; }
        .tab-btn.active { background: #111827; color: white; box-shadow: 0 2px 5px rgba(0,0,0,0.2); }
        .count-badge { background: #e5e7eb; color: #4b5563; font-size: 0.7rem; padding: 2px 8px; border-radius: 10px; font-weight: 700; }
        .count-badge.active { background: rgba(255,255,255,0.25); color: white; }

        /* --- Table --- */
        .shadow-custom { box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03); }
        .shadow-sm-bottom { box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
        .custom-table th { font-size: 0.75rem; color: #0044ff; padding: 14px 16px; border-bottom: 1px solid #e5e7eb; font-weight: 700; letter-spacing: 0.05em; background: #ffffff; text-transform: uppercase; }
        .custom-table td { padding: 12px 16px; border-bottom: 1px solid #f3f4f6; font-size: 0.9rem; vertical-align: middle; }
        .hover-row:hover { background-color: #f9fafb; }
        
        .location-cell { display: flex; align-items: center; gap: 6px; font-size: 0.85rem; color: #007259; }
        .btn-icon-soft { width: 32px; height: 32px; border-radius: 8px; border: 1px solid #e5e7eb; background: white; color: #4b5563; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
        .btn-icon-soft:hover { border-color: #d1d5db; background: #f3f4f6; color: #111827; }

        /* --- Action Buttons --- */
        .action-btn { padding: 6px 14px; border-radius: 20px; font-size: 0.8rem; font-weight: 600; display: flex; align-items: center; gap: 6px; border: 1px solid transparent; transition: 0.2s; white-space: nowrap; }
        .action-btn:hover { transform: translateY(-1px); box-shadow: 0 2px 5px rgba(0,0,0,0.05); }
        .action-btn.approve { background: #10b981; color: white; }
        .action-btn.reject { background: #fee2e2; color: #ef4444; }
        .action-btn.reject:hover { background: #fecaca; }
        .action-btn.verify { background: #2563eb; color: white; }
        .action-btn.verify:hover { background: #1d4ed8; }

        /* --- Modal Custom --- */
        .modal-backdrop-custom { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); backdrop-filter: blur(5px); display: flex; align-items: center; justify-content: center; z-index: 1050; animation: fadeIn 0.2s ease-out; }
        .modal-content-custom { background: white; width: 95%; border-radius: 16px; overflow: hidden; box-shadow: 0 20px 50px rgba(0,0,0,0.2); }
        .zoom-in { animation: zoomIn 0.2s ease-out; }
        
        .upload-box { border: 2px dashed #d1d5db; border-radius: 12px; padding: 24px; text-align: center; cursor: pointer; background: #f9fafb; transition: 0.2s; }
        .upload-box:hover { border-color: #6366f1; background: #eff6ff; }
        
        .option-card { border: 2px solid #e5e7eb; border-radius: 12px; padding: 15px; text-align: center; cursor: pointer; transition: 0.2s; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: #6b7280; font-size: 0.85rem; font-weight: 600; background: white; }
        .option-card:hover { border-color: #9ca3af; transform: translateY(-2px); }
        .option-card.active { border-color: #2563eb; background: #eff6ff; color: #2563eb; box-shadow: 0 4px 10px rgba(37, 99, 235, 0.1); }
        
        .btn-close-floating { position: absolute; top: 20px; right: 20px; width: 44px; height: 44px; background: white; border: none; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.3s; box-shadow: 0 4px 12px rgba(0,0,0,0.2); }
        .btn-close-floating:hover { background: #ef4444; color: white; transform: rotate(90deg); }
        
        .proof-card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 4px; background: white; transition: 0.2s; }
        .proof-card:hover { border-color: #6366f1; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        .proof-card .img-wrapper { height: 110px; border-radius: 6px; overflow: hidden; position: relative; background: #f3f4f6; }
        .proof-card img { width: 100%; height: 100%; object-fit: cover; }
        .hover-overlay { position: absolute; top:0; left:0; width:100%; height:100%; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; opacity: 0; transition: 0.2s; }
        .proof-card:hover .hover-overlay { opacity: 1; }

        .btn-white { background: white; color: #374151; }
        .btn-white:hover { background: #f9fafb; }

        .status-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 50px; font-size: 0.75rem; font-weight: 600; }
        .status-badge .dot { width: 6px; height: 6px; border-radius: 50%; }
        .status-badge.verified { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .status-badge.verified .dot { background: #15803d; }
        .status-badge.auto-verified { background: #ecfeff; color: #0891b2; border: 1px solid #a5f3fc; }
        .status-badge.auto-verified .dot { background: #0891b2; }
        
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes zoomIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .animate-fade-in { animation: fadeIn 0.3s ease-in-out; }
      `}</style>
    </div>
  );
};

export default AdminRefuelVerify;